import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Search, Loader2, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import {
  rechercherPdl, peutChercher, MESSAGE_INJOIGNABLE, MESSAGE_TROP_D_APPELS,
} from '@/shared/api/pdl';

// Le bouton qui va chercher le point de livraison, et la phrase qui dit ce qui s'est
// passe. UN seul exemplaire, pose dans les DEUX parcours.
//
// POURQUOI UN COMPOSANT ET PAS DEUX BOUTONS
//
// Le parcours installateur et le parcours d'auto-inscription posent la meme question et
// doivent y repondre avec les memes mots. Deux boutons ecrits separement finiraient par
// dire deux choses differentes du meme fait -- et le jour d'un correctif, un seul des
// deux le recevrait.
//
// CE QU'IL NE FAIT JAMAIS
//
// 1. Remplir le champ en silence. La valeur trouvee s'accompagne toujours d'une ligne
//    qui dit d'ou elle vient : sans elle, le poseur croit avoir saisi ce numero et ne
//    le verifie pas.
// 2. Proposer quelque chose quand la recherche ne trouve pas. Un PDL faux est pire
//    qu'un PDL absent : il a l'air juste, il sert de cle de deduplication des clients,
//    et il designe le compteur de quelqu'un d'autre.
// 3. Empecher de saisir. Le champ manuel reste la, toujours modifiable. Une recherche
//    qui echoue est un confort en moins, jamais un parcours bloque.
// Comment chaque champ corrige se nomme a l'ecran. Le nom technique ne dit rien a un
// poseur, et « complement_adresse : ESC A 3 ETG D » se lirait comme une erreur.
const LIBELLES = {
  prenom: 'Prenom',
  nom: 'Nom',
  adresse: 'Adresse',
  complement_adresse: "Complement d'adresse",
  code_postal: 'Code postal',
  ville: 'Ville',
};

export default function RecherchePdl({ client, valeurActuelle, onTrouve,
                                       onCorrigerChamps, authentifie = false,
                                       libelleBouton = 'Rechercher le PDL',
                                       testid = 'pdl-recherche' }) {
  const [enCours, setEnCours] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [echec, setEchec] = useState('');
  // Ce qu'on a corrige, et la valeur d'avant. Sans elle, « revenir a ma saisie » ne
  // pourrait pas revenir : le champ aurait deja ete ecrase.
  const [corrections, setCorrections] = useState(null);

  const champs = {
    nom: client.nom,
    prenom: client.prenom,
    adresse: client.adresse,
    complementAdresse: client.complement_adresse,
    codePostal: client.code_postal,
    ville: client.ville,
  };
  const pret = peutChercher(champs);

  const chercher = async () => {
    setEnCours(true);
    setEchec('');
    setVerdict(null);
    setCorrections(null);
    try {
      const resultat = await rechercherPdl(champs, { authentifie });
      setVerdict(resultat);
      if (resultat.trouve && resultat.pdl) onTrouve(resultat.pdl);
      // LA CORRECTION EST AUTOMATIQUE, arbitrage de Boubacar du 01/09. Elle est aussi
      // NOMMEE et REVERSIBLE : le compteur n'est pas toujours au nom du client --
      // locataire, heritage, nom de jeune fille -- et dans ces cas Enedis a raison sur
      // le compteur et tort sur le client. Corriger en silence rendrait ce cas
      // indetectable jusqu'au mandat.
      if (resultat.trouve && onCorrigerChamps) {
        const aEcrire = {};
        const avant = {};
        Object.entries(resultat.rapprochement?.champs || {}).forEach(([cle, etat]) => {
          if (!etat.proposition) return;
          avant[cle] = client[cle] || '';
          aEcrire[cle] = etat.proposition;
        });
        if (Object.keys(aEcrire).length) {
          onCorrigerChamps(aEcrire);
          setCorrections({ avant, apres: aEcrire });
        }
      }
    } catch (erreur) {
      // Le plafond de la route publique et une panne de reseau n'appellent pas le meme
      // geste : dans un cas on attend, dans l'autre on saisit tout de suite.
      setEchec(erreur?.response?.status === 429 ? MESSAGE_TROP_D_APPELS
        : MESSAGE_INJOIGNABLE);
    }
    setEnCours(false);
  };

  // Le numero affiche n'est plus celui qu'on a trouve : l'utilisateur l'a corrige, et
  // c'est son droit. On cesse alors de revendiquer la provenance, sinon la ligne
  // mentirait sur la valeur du champ.
  const corrigeALaMain = Boolean(
    verdict?.trouve && verdict.pdl && valeurActuelle && valeurActuelle !== verdict.pdl,
  );

  return (
    <div className="space-y-1">
      <Button
        type="button" variant="outline" size="sm" className="h-8 text-[12px]"
        onClick={chercher} disabled={enCours || !pret} data-testid={testid}
      >
        {enCours ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          : <Search className="w-3.5 h-3.5 mr-1" />}
        {libelleBouton}
      </Button>

      {!pret && (
        <p className="text-[10px] text-neutral-400" data-testid={`${testid}-prerequis`}>
          Renseignez le nom, l&apos;adresse, le code postal et la ville pour lancer la
          recherche.
        </p>
      )}

      {verdict?.trouve && !corrigeALaMain && (
        <p className="text-[11px] text-emerald-700 flex items-start gap-1"
           data-testid="pdl-provenance">
          <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {/* CE QUE LE COMPTEUR EST, ET S'IL EST VIVANT. Deux informations qu'Enedis rend
          et qu'on jetait : un compteur qui n'est pas un Linky ou qui n'est pas en
          service ne fera jamais remonter la moindre donnee, et mieux vaut l'apprendre
          sur le chantier que trois semaines plus tard sur un ecran vide. */}
      {verdict?.trouve && (verdict.fiche?.compteur_type || verdict.fiche?.compteur_etat) && (
        <p className="text-[11px] text-neutral-500" data-testid="pdl-compteur">
          Compteur
          {verdict.fiche.compteur_type ? ` ${verdict.fiche.compteur_type}` : ''}
          {verdict.fiche.compteur_etat ? ` · ${verdict.fiche.compteur_etat}` : ''}
        </p>
      )}

      {/* CHAQUE CHAMP CORRIGE EST NOMME, avec ce qu'il valait avant. Une correction
          silencieuse ferait signer un mandat au nom du proprietaire chez un locataire
          sans que personne ne l'ait vu passer. */}
      {corrections && (
        <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-100
                        rounded px-2 py-1.5 space-y-0.5"
             data-testid="pdl-corrections">
          <p className="flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>Corrige d&apos;apres Enedis :</span>
          </p>
          {Object.entries(corrections.apres).map(([cle, valeur]) => (
            <p key={cle} className="pl-4" data-testid={`pdl-correction-${cle}`}>
              <span className="text-neutral-500">{LIBELLES[cle] || cle}</span>
              {' : '}
              {corrections.avant[cle]
                ? <span className="line-through text-neutral-400">{corrections.avant[cle]}</span>
                : <span className="text-neutral-400">(vide)</span>}
              {' → '}
              <span className="font-medium">{valeur}</span>
            </p>
          ))}
          <button
            type="button"
            className="pl-4 underline text-neutral-500 hover:text-neutral-700"
            data-testid="pdl-annuler-corrections"
            onClick={() => { onCorrigerChamps(corrections.avant); setCorrections(null); }}
          >
            Revenir a ma saisie
          </button>
        </div>
      )}

      {/* Une divergence qu'on ne sait PAS corriger reste dite. Le titulaire en est le
          cas type : Enedis rend « BARRY Boubacar » d'un bloc, et deviner ou couper
          entre nom et prenom serait faux au premier nom compose. On montre les deux et
          on laisse trancher. */}
      {verdict?.trouve && verdict.rapprochement?.champs?.titulaire?.verdict === 'divergent' && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1"
           data-testid="pdl-titulaire-divergent">
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          Le compteur est au nom de «&nbsp;
          {verdict.rapprochement.champs.titulaire.enedis}
          &nbsp;», la fiche au nom de «&nbsp;
          {verdict.rapprochement.champs.titulaire.saisi}
          &nbsp;». Verifiez avant de continuer.
        </p>
      )}

      {corrigeALaMain && (
        <p className="text-[11px] text-neutral-500 flex items-start gap-1"
           data-testid="pdl-provenance">
          <Info className="w-3.5 h-3.5 mt-px shrink-0" />
          Numero corrige a la main : c&apos;est celui-ci qui sera enregistre.
        </p>
      )}

      {verdict && !verdict.trouve && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1"
           data-testid={`${testid}-message`}>
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {echec && (
        <p className="text-[11px] text-red-600 flex items-start gap-1"
           data-testid={`${testid}-message`}>
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          {echec}
        </p>
      )}
    </div>
  );
}
